/**
 * User Collaboration Hook
 *
 * The staff list for User settings (#68). Reads the organisation's
 * `user_profiles` rows directly, with a realtime subscription, and merges in
 * team roles from the membership list. Both are plain SDK calls, so the list
 * paints without waiting on a function cold start.
 *
 * Edits write the row directly: owners have `update` on every row in their
 * organisation. The user-profile-sync trigger carries name, phone and status
 * across to the Appwrite Auth user.
 *
 * If the table isn't there yet (an app deployed ahead of the schema), it falls
 * back to the old user-management `list-users` call, read-only for names.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dataApi } from '../services/api';
import appwrite, { COLLECTIONS } from '../services/appwrite';
import { sortOrderUpdatesForMove } from '../lib/userSortOrder';

const { getCurrentUser, getTeamMembers, subscribeToCollection } = appwrite;

/** The fields UsersTable renders, from a profile row plus its membership. */
function toUser(profile, member) {
  return {
    id: profile.userId,
    profileId: profile.$id,
    membershipId: member?.id || null,
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    email: profile.email || member?.userEmail || '',
    phone: profile.phone || '',
    sortOrder: Number(profile.sortOrder) || 0,
    status: (profile.status || 'active') !== 'blocked',
    invited: Boolean(profile.invitedAt) && !profile.acceptedAt,
    lastInviteSentAt: profile.lastInviteSentAt || null,
    teamRoles: member?.roles || []
  };
}

/** Old-shape rows from list-users, for the pre-schema fallback. */
function legacyToProfile(u, teamId, index) {
  const [first, ...rest] = String(u.name || u.userName || '').split(' ');
  return {
    $id: null,
    organisationId: teamId,
    userId: u.id || u.userId,
    firstName: first || '',
    lastName: rest.join(' '),
    email: u.email || u.userEmail || '',
    phone: u.phone || '',
    sortOrder: index,
    status: u.status === false ? 'blocked' : 'active'
  };
}

/**
 * @param {boolean} isActive - list active users (true) or deactivated ones (false)
 */
export function useUserCollaboration(isActive = true) {
  const [profiles, setProfiles] = useState([]);
  const [members, setMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legacy, setLegacy] = useState(false);
  const backfillTried = useRef(false);
  const membersRef = useRef(members);
  membersRef.current = members;

  const teamId = currentUser?.teamId;
  const canManage = Boolean(currentUser?.isTeamOwner) && !legacy;

  useEffect(() => {
    getCurrentUser()
      .then(setCurrentUser)
      .catch((err) => console.error('Error getting current user:', err));
  }, []);

  const loadUsers = useCallback(async () => {
    if (!teamId) return;
    setError(null);
    try {
      const [memberList, rows] = await Promise.all([
        getTeamMembers(),
        dataApi.userProfileList(teamId)
      ]);
      setMembers(memberList);
      setProfiles(rows);
      setLegacy(false);
      setLoading(false);

      // Members who joined before the table existed have no row yet. An owner's
      // first visit creates them; everyone else just sees the rows there are.
      const have = new Set(rows.map((r) => r.userId));
      const missing = memberList.some((m) => !have.has(m.userId));
      if (missing && currentUser?.isTeamOwner && !backfillTried.current) {
        backfillTried.current = true;
        const created = await dataApi.userProfilesBackfill();
        if (created > 0) setProfiles(await dataApi.userProfileList(teamId));
      }
    } catch (err) {
      console.warn('user_profiles unavailable, falling back to list-users:', err.message);
      try {
        const [memberList, legacyUsers] = await Promise.all([getTeamMembers(), dataApi.userList()]);
        setMembers(memberList);
        setProfiles(legacyUsers.map((u, i) => legacyToProfile(u, teamId, i)));
        setLegacy(true);
      } catch (fallbackErr) {
        console.error('Error loading users:', fallbackErr);
        setError(fallbackErr.message);
      }
      setLoading(false);
    }
  }, [teamId, currentUser?.isTeamOwner]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Live updates replace the old 60-second poll. Only rows this user can read
  // are delivered, but the organisation check keeps a multi-org user's other
  // teams out of this list.
  useEffect(() => {
    if (!teamId || legacy) return undefined;
    let unsubscribe = () => {};
    try {
      unsubscribe = subscribeToCollection(COLLECTIONS.USER_PROFILES, (response) => {
        const row = response.payload;
        if (!row || row.organisationId !== teamId) return;
        const deleted = response.events?.some((e) => e.endsWith('.delete'));
        setProfiles((prev) => {
          const rest = prev.filter((p) => p.$id !== row.$id);
          return deleted ? rest : [...rest, row];
        });
        // A new person (invited from another tab or by another owner) needs
        // their membership for roles.
        if (!deleted && !membersRef.current.some((m) => m.userId === row.userId)) {
          getTeamMembers().then(setMembers).catch(() => {});
        }
      });
    } catch (err) {
      console.warn('[Realtime] user_profiles subscription failed:', err.message);
    }
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [teamId, legacy]);

  const users = useMemo(() => {
    const byUser = new Map(members.map((m) => [m.userId, m]));
    return profiles
      // A row whose membership is gone is a removal in flight; hide it. The
      // membership list can lag a fresh invite, so a row with no member is
      // still shown when the list is empty.
      .filter((p) => members.length === 0 || byUser.has(p.userId))
      .map((p) => toUser(p, byUser.get(p.userId)))
      .filter((u) => (isActive ? u.status : !u.status))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [profiles, members, isActive]);

  /** Patch a profile locally, keyed by user id. */
  const patchProfile = useCallback((userId, data) => {
    setProfiles((prev) => prev.map((p) => (p.userId === userId ? { ...p, ...data } : p)));
  }, []);

  /**
   * Update one field. Optimistic: the list changes at once and is put back if
   * the write fails.
   *
   * @param {string} userId
   * @param {'firstName'|'lastName'|'phone'|'status'|'labels'} field
   */
  const updateUser = useCallback(async (userId, field, value) => {
    if (field === 'labels' || field === 'roles') {
      return dataApi.userUpdateLabels(userId, value);
    }

    const profile = profiles.find((p) => p.userId === userId);
    if (!profile?.$id) throw new Error('This user cannot be edited yet');

    let data;
    switch (field) {
      case 'firstName': {
        const first = String(value || '').trim();
        if (!first) throw new Error('First name is required');
        data = { firstName: first };
        break;
      }
      case 'lastName':
        data = { lastName: String(value || '').trim() || null };
        break;
      case 'phone':
        data = { phone: String(value || '').trim() || null };
        break;
      case 'status':
        data = { status: value ? 'active' : 'blocked' };
        break;
      default:
        throw new Error(`Unknown field: ${field}`);
    }

    const previous = Object.fromEntries(Object.keys(data).map((k) => [k, profile[k]]));
    patchProfile(userId, data);
    try {
      const saved = await dataApi.userProfileUpdate(profile.$id, data);
      patchProfile(userId, saved);
      return saved;
    } catch (err) {
      patchProfile(userId, previous);
      throw err;
    }
  }, [profiles, patchProfile]);

  /** Drag reorder within this list. Writes only the rows whose order changed. */
  const reorderUsers = useCallback(async (activeUserId, overUserId) => {
    const listed = users.map((u) => ({ id: u.id, sortOrder: u.sortOrder }));
    const updates = sortOrderUpdatesForMove(listed, activeUserId, overUserId);
    if (updates.length === 0) return;

    const before = profiles;
    const byUser = new Map(updates.map((u) => [u.id, u.sortOrder]));
    setProfiles((prev) => prev.map((p) => (
      byUser.has(p.userId) ? { ...p, sortOrder: byUser.get(p.userId) } : p
    )));
    try {
      await Promise.all(updates.map((u) => {
        const profile = profiles.find((p) => p.userId === u.id);
        return dataApi.userProfileUpdate(profile.$id, { sortOrder: u.sortOrder });
      }));
    } catch (err) {
      setProfiles(before);
      throw err;
    }
  }, [users, profiles]);

  const resendInvite = useCallback(async (userId) => {
    const saved = await dataApi.userResendInvite(userId);
    if (saved) patchProfile(userId, saved);
    return saved;
  }, [patchProfile]);

  /** Roles are membership data, edited through UserRolesForm. */
  const setMemberRoles = useCallback((userId, roles) => {
    setMembers((prev) => prev.map((m) => (m.userId === userId ? { ...m, roles } : m)));
  }, []);

  return {
    users,
    currentUser,
    canManage,
    legacy,
    loading,
    isLoading: loading,
    error,
    updateUser,
    reorderUsers,
    resendInvite,
    setMemberRoles,
    refresh: loadUsers
  };
}

export default useUserCollaboration;
